/**
 * Applies accessibility fixes to all HTML tables found in an arbitrary HTML
 * fragment (e.g., a full QTI itemBody string). Unlike the ready-react17
 * sanitizeTableHtml utility, this function:
 *  - Works on a full HTML fragment that may contain zero or more tables
 *  - Does NOT extract or unwrap tables (tables here come from QTI XML, not
 *    SunEditor, so no div-wrapping or editor wrapper issues exist)
 *  - Injects scope="col" on complex table header cells
 *  - Strips redundant colspan="1" and rowspan="1" default attributes
 *
 * Steps per table:
 *  1. Check complexity BEFORE stripping: any thead th has colspan or rowspan
 *     (with a value other than "1")?
 *  2. If complex → set scope="col" on every thead th that lacks one.
 *  3. Remove colspan="1" and rowspan="1" throughout the table.
 */
export function applyTableAccessibility(html: string): string {
  if (!html) return html

  // Skip the DOMParser round-trip if the fragment contains no tables at all
  if (!html.includes("<table") && !html.includes("<TABLE")) return html

  const doc = new DOMParser().parseFromString(html, "text/html")
  const tables = Array.from(doc.querySelectorAll("table"))

  if (tables.length === 0) return html

  tables.forEach(table => {
    const theadThs = Array.from(table.querySelectorAll("thead th"))

    // Step 1 — complexity check before attribute mutation
    const isComplex = theadThs.some(th => {
      const colspan = th.getAttribute("colspan")
      const rowspan = th.getAttribute("rowspan")
      return (colspan !== null && colspan !== "1") ||
        (rowspan !== null && rowspan !== "1")
    })

    // Step 2 — inject scope="col" on complex header cells
    if (isComplex) {
      theadThs.forEach(th => {
        if (!th.hasAttribute("scope")) {
          th.setAttribute("scope", "col")
        }
      })
    }

    // Step 3 — strip redundant default attributes
    table.querySelectorAll("[colspan='1']").forEach(el =>
      el.removeAttribute("colspan")
    )
    table.querySelectorAll("[rowspan='1']").forEach(el =>
      el.removeAttribute("rowspan")
    )
  })

  return doc.body.innerHTML
}
